import type { StatusTone } from "@/components/ui/status-badge";

import type {
  AuthorityWeight,
  LibraryHit,
  ResearchSource,
  ResearchSourceId
} from "./types";

/**
 * The library's sources, in the order the page and the composer offer them.
 * They match `research_library_service.SOURCES` and
 * `case_agent.research_scope.SOURCE_TOOLS`.
 */
export const RESEARCH_SOURCES: ResearchSource[] = [
  {
    id: "case_law",
    label: "Case Law",
    blurb: "Published BIA, AG, AAO, circuit and Supreme Court decisions",
    authority:
      "Published precedent. The court on each decision says who it binds.",
    examples: [
      "Matter of Dhanasar national interest waiver",
      "Particular social group firm resettlement"
    ]
  },
  {
    id: "statutes",
    label: "Statutes & Regs",
    blurb: "The INA, 8 U.S.C. and 8 C.F.R.",
    authority: "The INA, 8 U.S.C. and 8 C.F.R. The text controls.",
    examples: ["INA 245 adjustment of status", "8 CFR 204.5(h)"]
  },
  {
    id: "references",
    label: "Policy & Practice Manuals",
    blurb: "The USCIS Policy Manual and EOIR practice manuals",
    authority:
      "Agency guidance. The Policy Manual binds adjudicators, not judges.",
    examples: ["O-1 comparable evidence", "6 USCIS-PM F 2"]
  },
  {
    id: "unpublished",
    label: "Unpublished & Non-Precedent",
    blurb: "Unpublished BIA and AAO non-precedent decisions",
    authority:
      "Persuasive only. Never cite these as Matter of or as AAO precedent.",
    examples: [
      "Judging the work of others O-1A",
      "Critical role for a distinguished organization"
    ]
  },
  {
    id: "district",
    label: "District Courts",
    blurb: "District-court immigration decisions",
    authority: "Not precedent unless a higher court adopted the holding.",
    examples: ["Unreasonable delay mandamus I-485", "APA review of NIW denial"]
  },
  {
    id: "updates",
    label: "Regulatory Updates",
    blurb: "Federal Register notices and Policy Manual alerts",
    authority:
      "Federal Register notices and Policy Manual alerts. The C.F.R. text is still the rule.",
    examples: ["H-1B fee", "Public charge"],
    browsable: true
  }
];

export const RESEARCH_SOURCE_IDS: readonly ResearchSourceId[] =
  RESEARCH_SOURCES.map((s) => s.id);

/** What All Sources searches side by side, in the order it shows them. */
export const ALL_SOURCE_IDS: readonly ResearchSourceId[] = [
  "case_law",
  "statutes",
  "references",
  "unpublished"
];

export const sourceById = (id: ResearchSourceId): ResearchSource =>
  RESEARCH_SOURCES.find((s) => s.id === id) ?? RESEARCH_SOURCES[0];

export const WEIGHT: Record<
  AuthorityWeight,
  { label: string; tone: StatusTone; hint: string }
> = {
  controlling: {
    label: "Controlling",
    tone: "success",
    hint: "Statute or regulation: the text governs."
  },
  precedent: {
    label: "Precedent",
    tone: "accent",
    hint: "Published decision. Binding where its court is."
  },
  persuasive: {
    label: "Persuasive Only",
    tone: "warn",
    hint: "Unpublished, non-precedent or district court. Binds no one."
  },
  guidance: {
    label: "Guidance",
    tone: "info",
    hint: "Agency guidance. Binds adjudicators, not judges."
  }
};

/** The circuits Case Law can be biased toward. */
export const CIRCUITS = [
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "11th",
  "D.C."
] as const;

const yearOf = (date: string | null) =>
  /(?:19|20)\d{2}/.exec(date ?? "")?.[0] ?? null;

/**
 * An authority as prose cites it: "Matter of DHANASAR, 26 I&N Dec. 884 (AAO
 * 2016)". The citation and year are added only when the title lacks them.
 */
export const citeHit = (
  hit: Pick<LibraryHit, "title" | "citation" | "date">
): string => {
  const citation = hit.citation?.trim();
  const base =
    citation && !hit.title.includes(citation)
      ? `${hit.title}, ${citation}`
      : hit.title;
  const year = yearOf(hit.date);
  return year && !/\(\D*(?:19|20)\d{2}\)$/.test(base)
    ? `${base} (${year})`
    : base;
};

/** "Ninth Circuit" out of "Court of Appeals for the Ninth Circuit". */
export const courtLabel = (
  hit: Pick<LibraryHit, "court" | "circuit">
): string | null => {
  const court = hit.court?.replace(/^Court of Appeals for the /, "") ?? null;
  return court || hit.circuit || null;
};

const CITATION_SHAPES = [
  /USCIS-PM|\bICPM\b|\bBIA\s?PM\b|practice manual/i,
  /\d+\s+I\s*&\s*N\s+Dec\.?\s*\d+|\d+\s+F\.\s?(?:2d|3d|4th)\s+\d+|\d+\s+F\.\s?Supp|\d+\s+U\.S\.\s+\d+|\d+\s+S\.\s?Ct\.\s+\d+/i,
  /\bINA\s*§?\s*\d|\bU\.?\s?S\.?\s?C\.?\s*§?\s*\d|\bC\.?\s?F\.?\s?R\.?\s*(?:§|part)?\s*\d|§\s*\d/i
];

/** True when the query reads as a citation the library can resolve. */
export const looksLikeCitation = (query: string): boolean => {
  const text = query.trim();
  return (
    text.length > 0 &&
    text.length <= 80 &&
    CITATION_SHAPES.some((shape) => shape.test(text))
  );
};
